import { ReactElement, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowLeft, EllipsisVertical, Loader2, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import * as Editable from '@/components/ui/editable'
import { Kbd } from '@/components/ui/kbd'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { deleteProduct, getProductById, updateProduct } from '@/lib/productService'
import {
  NEEDS_REVIEW_QUERY_KEY,
  NEEDS_REVIEW_COUNT_QUERY_KEY,
} from '@/features/sku-alias/api/needsReview'
import { ScrollReveal, ImageUrlPopover, CostInput, costStringSchema, parseCostToNumber, formatCostFromNumber, DatePicker } from '@/shared/components/ui'
import { updateVariant } from '@/lib/variantService'
import { Label } from '@/components/ui/label'
import { format } from 'date-fns'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { ProductVariantsManager, type ProductVariantsManagerHandle, type ViewMode } from '@/features/products/components/ProductVariantsManager'
import { ProductActionBar } from '@/features/products/components/ProductActionBar'
import { MatchedProductIdsCard } from '@/features/products/components/MatchedProductIdsCard'
import { ProductTagsSection } from '@/features/tags/components/ProductTagsSection'

const productFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  brand: z.string().optional(),
})

/** Extended schema for discrete variant products (includes SKU, Cost fields) */
const discreteProductFormSchema = productFormSchema.extend({
  sku: z.string().min(1, 'SKU is required'),
  originalCost: costStringSchema,
  originalCurrency: z.string().optional(),
  costDate: z.string().optional().nullable(),
})

type ProductFormValues = z.infer<typeof productFormSchema>
type DiscreteProductFormValues = z.infer<typeof discreteProductFormSchema>

export function ProductAdminPage(): ReactElement {
  const { t } = useTranslation('features-products')
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { activeOrgId } = useActiveOrg()

  // Read variantId from URL query param (e.g. ?variantId=123)
  const initialVariantId = searchParams.get('variantId')
    ? Number(searchParams.get('variantId'))
    : null

  // Refs for coordinated form handling
  const variantManagerRef = useRef<ProductVariantsManagerHandle>(null)
  const [variantFormDirty, setVariantFormDirty] = useState(false)
  const [variantFormSaving, setVariantFormSaving] = useState(false)
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null)
  const [variantViewMode, setVariantViewMode] = useState<ViewMode>('edit')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', id, activeOrgId],
    queryFn: () => getProductById(id!),
    enabled: !!id && !!activeOrgId,
  })

  // Detect discrete variant mode: single variant with isDiscreteVariant flag
  const discreteVariant = product?.variants.find(v => v.isDiscreteVariant && !v.archived)
  const isDiscreteMode = !!discreteVariant

  // Schema depends on mode: discrete products validate SKU/cost; multi-variant products don't
  // have those fields. isDiscreteMode is derived from server data and stable per page load,
  // so swapping the resolver once when the product loads is safe.
  const resolver = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- zod resolver type inference limitation
    () => zodResolver(isDiscreteMode ? discreteProductFormSchema : productFormSchema) as any,
    [isDiscreteMode]
  )
  const form = useForm<DiscreteProductFormValues>({
    resolver,
    defaultValues: {
      name: '',
      description: '',
      brand: '',
      sku: '',
      originalCost: '',
      originalCurrency: 'PLN',
      costDate: '',
    },
  })

  // Watch cost fields for CostInput component (avoids inline form.watch() which breaks React Compiler)
  const watchedCost = useWatch({ control: form.control, name: 'originalCost' })
  const watchedCurrency = useWatch({ control: form.control, name: 'originalCurrency' })
  const watchedCostDate = useWatch({ control: form.control, name: 'costDate' })

  const updateMutation = useMutation({
    mutationFn: (values: ProductFormValues) =>
      updateProduct(id!, values),
    onSuccess: () => {
      toast.success(t('admin.toasts.updateSuccess'))
      queryClient.invalidateQueries({ queryKey: ['product', id] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: (err) => {
      toast.error(t('admin.toasts.updateError'))
      console.error(err)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteProduct(id!),
    onSuccess: async () => {
      toast.success(t('admin.toasts.deleteSuccess'))
      setShowDeleteDialog(false)
      // Invalidate NeedsReview queries since deletion may create new unmatched listings
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_COUNT_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ['products'] }),
      ])
      navigate('/dashboard/management/products')
    },
    onError: (err) => {
      toast.error(t('admin.toasts.deleteError'))
      console.error(err)
    },
  })

  useEffect(() => {
    if (product) {
      const baseValues = {
        name: product.name,
        description: product.description ?? '',
        brand: product.brand ?? '',
      }

      if (discreteVariant) {
        // Include discrete variant fields
        form.reset({
          ...baseValues,
          sku: discreteVariant.sku ?? '',
          originalCost: formatCostFromNumber(discreteVariant.originalCost),
          originalCurrency: discreteVariant.originalCurrency ?? 'PLN',
          costDate: discreteVariant.costDate ? format(new Date(discreteVariant.costDate), 'yyyy-MM-dd') : '',
        })
      } else {
        form.reset(baseValues)
      }
    }
  }, [product, discreteVariant, form])

  // Computed: whether any form has unsaved changes
  // In discrete mode, all fields are in the main form; in variants mode, track both forms
  const hasChanges = isDiscreteMode
    ? form.formState.isDirty
    : form.formState.isDirty || variantFormDirty
  const isSaving = updateMutation.isPending || variantFormSaving

  // Coordinated save: save both product and variant forms
  const handleSaveAll = useCallback(async () => {
    const promises: Promise<unknown>[] = []

    // Save product form if dirty
    if (form.formState.isDirty) {
      promises.push(
        new Promise<void>((resolve, reject) => {
          form.handleSubmit(
            async (values) => {
              try {
                // Update product
                await updateProduct(id!, {
                  name: values.name,
                  description: values.description,
                  brand: values.brand,
                })

                // Update discrete variant if in discrete mode
                if (isDiscreteMode && discreteVariant) {
                  await updateVariant(discreteVariant.id, {
                    sku: values.sku,
                    originalCost: parseCostToNumber(values.originalCost),
                    originalCurrency: values.originalCurrency || null,
                    costDate: values.costDate || null,
                  })
                }

                toast.success(t('admin.toasts.updateSuccess'))
                queryClient.invalidateQueries({ queryKey: ['product', id] })
                queryClient.invalidateQueries({ queryKey: ['products'] })
                resolve()
              } catch (err) {
                toast.error(t('admin.toasts.updateError'))
                console.error(err)
                reject(err)
              }
            },
            () => reject(new Error('Product form validation failed'))
          )()
        })
      )
    }

    // Save variant form if dirty (only for non-discrete mode)
    if (!isDiscreteMode && variantFormDirty && variantManagerRef.current) {
      promises.push(variantManagerRef.current.submit())
    }

    await Promise.all(promises)
  }, [form, id, isDiscreteMode, discreteVariant, variantFormDirty, queryClient, t])

  // Coordinated cancel: reset both forms
  const handleCancelAll = useCallback(() => {
    // Reset product form
    if (product) {
      const baseValues = {
        name: product.name,
        description: product.description ?? '',
        brand: product.brand ?? '',
      }

      if (discreteVariant) {
        form.reset({
          ...baseValues,
          sku: discreteVariant.sku ?? '',
          originalCost: formatCostFromNumber(discreteVariant.originalCost),
          originalCurrency: discreteVariant.originalCurrency ?? 'PLN',
          costDate: discreteVariant.costDate ? format(new Date(discreteVariant.costDate), 'yyyy-MM-dd') : '',
        })
      } else {
        form.reset(baseValues)
      }
    }

    // Reset variant form (only for non-discrete mode)
    if (!isDiscreteMode) {
      variantManagerRef.current?.reset()
    }
  }, [form, product, discreteVariant, isDiscreteMode])

  // Change description for action bar
  const changeDescription = (() => {
    const productDirty = form.formState.isDirty
    // In discrete mode, all changes are product-level
    if (isDiscreteMode) {
      return productDirty ? t('actionBar.productModified') : undefined
    }
    if (productDirty && variantFormDirty) return t('actionBar.productAndVariantModified')
    if (productDirty) return t('actionBar.productModified')
    if (variantFormDirty) return t('actionBar.variantModified')
    return undefined
  })()

  // Esc to go back (only when no unsaved changes)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !hasChanges) {
        event.preventDefault()
        navigate(-1)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [hasChanges, navigate])

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <p className="text-destructive">{t('admin.loadError')}</p>
        <Button onClick={() => navigate('/dashboard/management/products')}>{t('admin.goBack')}</Button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <ProductActionBar
        hasChanges={hasChanges}
        isSaving={isSaving}
        changeDescription={changeDescription}
        onSave={handleSaveAll}
        onCancel={handleCancelAll}
      />

      <div className="max-w-screen-lg mx-auto px-4 lg:px-8 pt-24 pb-8 space-y-8">
        <ScrollReveal blur>
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="h-5 w-5" />
              <Kbd>Esc</Kbd>
            </Button>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <Form {...form}>
            <form>
              <section className="flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* Left Column: Image */}
                  <div className="md:col-span-1 space-y-6">
                    <div>
                      <ImageUrlPopover
                        trigger={
                          <div className="aspect-square relative rounded-md overflow-hidden border border-border bg-muted/30 mb-4 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity">
                            {product.imageUrl ? (
                              <img
                                src={product.imageUrl}
                                alt={product.name}
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <span className="text-muted-foreground text-sm">{t('admin.clickToSetImage')}</span>
                            )}
                          </div>
                        }
                        currentImageUrl={product.imageUrl}
                        title={t('admin.imagePopover.title')}
                        description={t('admin.imagePopover.description')}
                        inputAriaLabel={t('admin.imagePopover.inputLabel')}
                        onSave={async (url) => {
                          await updateProduct(product.id, { imageUrl: url || undefined })
                          queryClient.invalidateQueries({ queryKey: ['product', id] })
                          queryClient.invalidateQueries({ queryKey: ['products'] })
                        }}
                        successMessage={t('admin.toasts.imageUpdateSuccess')}
                        errorMessage={t('admin.toasts.imageUpdateError')}
                      />
                    </div>
                  </div>

                  {/* Right Column: Details Form */}
                  <div className="md:col-span-2">
                    <div className="space-y-6">
                        {/* Product name header + SKU (discrete mode) */}
                        <div>
                          <div className="flex items-center justify-between">
                            <FormField
                              control={form.control}
                              name="name"
                              render={({ field }) => (
                                <Editable.Root
                                  value={field.value}
                                  onValueChange={field.onChange}
                                  triggerMode="click"
                                  submitOnBlur={false}
                                >
                                  <Editable.Area className="w-full flex items-center gap-1 group">
                                    <Editable.Preview className="text-2xl md:text-2xl font-bold tracking-tight px-2 py-1 border-none rounded-md focus-visible:ring-0 cursor-text" />
                                    <Editable.Trigger className="text-muted-foreground/30 hover:text-foreground transition-colors focus:outline-none">
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Editable.Trigger>
                                    <Editable.Input className="text-2xl md:text-2xl font-bold tracking-tight px-2 py-1 h-auto w-full" />
                                  </Editable.Area>
                                </Editable.Root>
                              )}
                            />
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="size-8 shrink-0">
                                  <EllipsisVertical className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  variant="destructive"
                                  onClick={() => setShowDeleteDialog(true)}
                                >
                                  <Trash2 className="size-4" />
                                  {t('admin.actions.delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          {/* SKU editable - discrete mode only, hidden when creating new variant */}
                          {isDiscreteMode && variantViewMode !== 'create' && (
                            <FormField
                              control={form.control}
                              name="sku"
                              render={({ field }) => (
                                <Editable.Root
                                  value={field.value}
                                  onValueChange={field.onChange}
                                  triggerMode="click"
                                  submitOnBlur={false}
                                >
                                  <Editable.Area className="w-full flex items-center gap-1 group -mt-1">
                                    <Editable.Preview className="text-sm text-muted-foreground px-2 py-1 border-none rounded-md focus-visible:ring-0 cursor-text truncate" />
                                    <Editable.Trigger className="text-muted-foreground/30 hover:text-foreground transition-colors focus:outline-none shrink-0">
                                      <Pencil className="h-3 w-3" />
                                    </Editable.Trigger>
                                    <Editable.Input className="text-sm text-muted-foreground px-2 py-1 h-auto w-full" />
                                  </Editable.Area>
                                </Editable.Root>
                              )}
                            />
                          )}
                        </div>

                        {/* Cost fields - discrete mode only, hidden when creating new variant */}
                        {isDiscreteMode && variantViewMode !== 'create' && (
                          <div className="grid grid-cols-2 gap-6">
                            <CostInput
                              costValue={watchedCost ?? ''}
                              onCostChange={(val) => form.setValue('originalCost', val, { shouldDirty: true })}
                              currencyValue={watchedCurrency}
                              onCurrencyChange={(val) => form.setValue('originalCurrency', val, { shouldDirty: true })}
                            />

                            <div className="space-y-2">
                              <Label>{t('variants.fields.costDate')}</Label>
                              <DatePicker
                                value={watchedCostDate ? new Date(watchedCostDate) : null}
                                onChange={(date) => form.setValue('costDate', date ? format(date, 'yyyy-MM-dd') : '', { shouldDirty: true })}
                                placeholder={t('variants.placeholders.selectDate')}
                              />
                            </div>
                          </div>
                        )}

                        <FormField
                          control={form.control}
                          name="brand"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('admin.fields.brand')}</FormLabel>
                              <FormControl>
                                <Input placeholder={t('admin.placeholders.brand')} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('admin.fields.description')}</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder={t('admin.placeholders.description')}
                                  className="min-h-[120px]"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <ProductTagsSection productId={product.id} />
                      </div>
                  </div>
                </div>
              </section>
            </form>
          </Form>
        </ScrollReveal>

        {/* Variants section - always show, works for both discrete and multi-variant products */}
        <ScrollReveal delay={0.2}>
          <ProductVariantsManager
            ref={variantManagerRef}
            product={product}
            onDirtyChange={setVariantFormDirty}
            onSavingChange={setVariantFormSaving}
            onSelectedVariantChange={setSelectedVariantId}
            onViewModeChange={setVariantViewMode}
            initialSelectedVariantId={initialVariantId}
          />
        </ScrollReveal>

        <ScrollReveal delay={0.3}>
          <MatchedProductIdsCard
            variantId={selectedVariantId}
            variantName={product.variants.find(v => v.id === selectedVariantId)?.name}
          />
        </ScrollReveal>

      </div>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('admin.deleteDialog.title')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {t('admin.deleteDialog.description')}
          </p>
          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteMutation.isPending}
            >
              {t('admin.deleteDialog.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? t('admin.deleteDialog.deleting')
                : t('admin.deleteDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
